import { type ReactNode } from 'react';

import styles from './Alert.module.css';

export type AlertTone = 'info' | 'success' | 'warning' | 'danger';

const ICONS: Record<AlertTone, ReactNode> = {
    info: <path d="M12 16v-5M12 8.2v.4" strokeWidth="2" strokeLinecap="round" />,
    success: (
        <path
            d="m7.5 12.4 3.1 3.1 6-6.6"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    ),
    warning: <path d="M12 8v5M12 16.2v.4" strokeWidth="2" strokeLinecap="round" />,
    danger: <path d="m9 9 6 6m0-6-6 6" strokeWidth="2" strokeLinecap="round" />,
};

/**
 * What an alert is allowed to say.
 *
 * `ReactNode` minus every value that renders nothing: `undefined`, `null`, and
 * the booleans a `&&` leaves behind. An alert exists in order to say something,
 * and this refuses the one shape that says nothing — an error branch whose whole
 * body is `{message}` where `message` is `string | undefined`, which paints a
 * red box with a title and empty space under it.
 *
 * The type is the enforcement point because the property is a data-flow one, and
 * data flow is what a type checker does. `check:states` cannot see it: it reads
 * a file as text and asks whether failure is *mentioned*, which an empty box
 * satisfies. Written this way the compiler either asks for a `??` fallback or
 * accepts the narrowing the careful call sites already do — inside
 * `{formError !== undefined && <Alert>{formError}</Alert>}` the identifier is
 * `string`, so those pass untouched.
 *
 * It reaches a *single* child only. JSX with two or more children hands over a
 * `ReactNode[]`, and `Iterable<ReactNode>` admits one containing `undefined`;
 * likewise `items={[]}` with nothing else is still an empty box. Those two are a
 * reviewer's job — `docs/architecture/testing-strategy.md` says which.
 */
export type AlertBody = Exclude<ReactNode, undefined | null | boolean>;

export interface AlertProps {
    tone?: AlertTone;
    title?: ReactNode;
    children?: AlertBody;
    /** Rendered as a bullet list; used for grouped validation failures. */
    items?: readonly string[];
    /**
     * Announce the message to assistive technology when it appears. Use for
     * anything that results from a user action; leave off for static notices.
     */
    live?: boolean;
}

export function Alert({ tone = 'info', title, children, items, live = false }: AlertProps) {
    return (
        <div
            className={`${styles.alert} ${styles[tone]}`}
            role={live ? (tone === 'danger' ? 'alert' : 'status') : undefined}
        >
            <svg
                className={styles.icon}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                aria-hidden="true"
            >
                <circle cx="12" cy="12" r="9.25" strokeWidth="1.5" />
                {ICONS[tone]}
            </svg>

            <div className={styles.body}>
                {title !== undefined && <strong className={styles.title}>{title}</strong>}
                {children}
                {items !== undefined && items.length > 0 && (
                    <ul className={styles.list}>
                        {items.map((item) => (
                            <li key={item}>{item}</li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
